import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "content", "previousButton", "nextButton", "progress", "progressTrack"]
  static values = { loop: Boolean }

  connect() {
    this.isDragging = false
    this.didDrag = false
    this.cancelClick = false
    this.startX = 0
    this.startScrollLeft = 0
    this.segmentWidth = 0
    this.loopStart = 0
    this.cloneCopies = 2
    this.dragThreshold = 6
    this.originalMarkup = this.hasContentTarget ? this.contentTarget.innerHTML : ""

    this.handlePointerDown = this.pointerDown.bind(this)
    this.handlePointerMove = this.pointerMove.bind(this)
    this.handlePointerUp = this.pointerUp.bind(this)
    this.handleClick = this.click.bind(this)
    this.handleNativeDrag = this.preventNativeDrag.bind(this)
    this.handleScroll = this.updateState.bind(this)
    this.handleResize = this.refreshLayout.bind(this)

    this.setupContent()

    this.scrollElement.addEventListener("pointerdown", this.handlePointerDown)
    this.scrollElement.addEventListener("pointermove", this.handlePointerMove)
    this.scrollElement.addEventListener("click", this.handleClick, true)
    this.scrollElement.addEventListener("dragstart", this.handleNativeDrag)
    this.scrollElement.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("pointerup", this.handlePointerUp)
    window.addEventListener("pointercancel", this.handlePointerUp)
    window.addEventListener("resize", this.handleResize)

    requestAnimationFrame(() => this.refreshLayout())
  }

  disconnect() {
    this.scrollElement.removeEventListener("pointerdown", this.handlePointerDown)
    this.scrollElement.removeEventListener("pointermove", this.handlePointerMove)
    this.scrollElement.removeEventListener("click", this.handleClick, true)
    this.scrollElement.removeEventListener("dragstart", this.handleNativeDrag)
    this.scrollElement.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("pointerup", this.handlePointerUp)
    window.removeEventListener("pointercancel", this.handlePointerUp)
    window.removeEventListener("resize", this.handleResize)
  }

  scrollLeft() {
    this.scrollBy(-1)
  }

  scrollRight() {
    this.scrollBy(1)
  }

  scrollBy(direction) {
    const distance = this.firstCard
      ? this.firstCard.getBoundingClientRect().width + this.trackGap()
      : this.scrollElement.clientWidth * 0.85

    this.scrollElement.scrollBy({
      left: distance * direction,
      behavior: "smooth"
    })
  }

  updateState() {
    this.normalizeLoopPosition()
    this.updateControls()
    this.updateProgress()
  }

  refreshLayout() {
    this.setupContent()
    this.measureLoop()

    if (this.canLoopSeamlessly) {
      this.scrollElement.scrollLeft = this.loopStart
    }

    this.updateState()
  }

  preventNativeDrag(event) {
    event.preventDefault()
  }

  pointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return

    this.isDragging = true
    this.didDrag = false
    this.cancelClick = false
    this.startX = event.clientX
    this.startScrollLeft = this.scrollElement.scrollLeft
  }

  pointerMove(event) {
    if (!this.isDragging) return

    const deltaX = event.clientX - this.startX
    if (!this.didDrag && Math.abs(deltaX) > this.dragThreshold) {
      this.didDrag = true
      this.cancelClick = true
      this.scrollElement.classList.remove("cursor-grab")
      this.scrollElement.classList.add("cursor-grabbing")
      this.scrollElement.setPointerCapture(event.pointerId)
    }

    if (!this.didDrag) return

    this.scrollElement.scrollLeft = this.startScrollLeft - deltaX
    event.preventDefault()
  }

  pointerUp(event) {
    if (!this.isDragging) return

    this.isDragging = false
    this.scrollElement.classList.remove("cursor-grabbing")
    this.scrollElement.classList.add("cursor-grab")

    if (event.pointerId && this.scrollElement.hasPointerCapture(event.pointerId)) {
      this.scrollElement.releasePointerCapture(event.pointerId)
    }
  }

  click(event) {
    if (!this.cancelClick) return

    event.preventDefault()
    event.stopPropagation()
    this.cancelClick = false
  }

  updateControls() {
    const hasOverflow = this.scrollElement.scrollWidth > this.scrollElement.clientWidth + 4
    const atStart = this.scrollElement.scrollLeft <= 4
    const atEnd = this.scrollElement.scrollLeft >= this.scrollElement.scrollWidth - this.scrollElement.clientWidth - 4

    if (this.hasPreviousButtonTarget) {
      const disabled = !this.canLoopSeamlessly && (!hasOverflow || atStart)
      this.previousButtonTarget.disabled = disabled
      this.previousButtonTarget.setAttribute("aria-disabled", String(disabled))
      this.previousButtonTarget.classList.toggle("!hidden", !hasOverflow)
    }

    if (this.hasNextButtonTarget) {
      const disabled = !this.canLoopSeamlessly && (!hasOverflow || atEnd)
      this.nextButtonTarget.disabled = disabled
      this.nextButtonTarget.setAttribute("aria-disabled", String(disabled))
      this.nextButtonTarget.classList.toggle("!hidden", !hasOverflow)
    }
  }

  updateProgress() {
    if (!this.hasProgressTarget || !this.hasProgressTrackTarget) return

    const viewportWidth = this.scrollElement.clientWidth
    const contentWidth = this.canLoopSeamlessly ? this.segmentWidth + viewportWidth : this.scrollElement.scrollWidth
    const effectiveMaxScrollLeft = this.canLoopSeamlessly
      ? Math.max(this.segmentWidth, 0)
      : Math.max(this.scrollElement.scrollWidth - viewportWidth, 0)
    const hasOverflow = effectiveMaxScrollLeft > 4

    this.progressTrackTarget.classList.toggle("!hidden", !hasOverflow)
    if (!hasOverflow) {
      this.progressTarget.style.width = "100%"
      this.progressTarget.style.transform = "translateX(0px)"
      return
    }

    const trackWidth = this.progressTrackTarget.clientWidth

    if (trackWidth <= 0 || contentWidth <= 0) {
      this.progressTarget.style.width = "100%"
      this.progressTarget.style.transform = "translateX(0px)"
      return
    }

    const visibleRatio = Math.min(viewportWidth / contentWidth, 1)
    const thumbWidth = Math.max(trackWidth * visibleRatio, 24)
    const maxOffset = trackWidth - thumbWidth
    const scrollLeft = this.canLoopSeamlessly
      ? this.normalizedLoopScrollLeft
      : Math.min(Math.max(this.scrollElement.scrollLeft, 0), effectiveMaxScrollLeft)
    const progress = effectiveMaxScrollLeft <= 0 ? 0 : Math.min(scrollLeft / effectiveMaxScrollLeft, 1)

    this.progressTarget.style.width = `${thumbWidth}px`
    this.progressTarget.style.transform = `translateX(${maxOffset * progress}px)`
  }

  setupContent() {
    if (!this.hasContentTarget) return

    if (!this.loopValue || !this.originalCards.length) {
      this.contentTarget.innerHTML = this.originalMarkup
      return
    }

    if (this.contentTarget.dataset.loopPrepared == "true") return

    const copies = []
    for (let index = 0; index < (this.cloneCopies * 2) + 1; index += 1) {
      const wrappedCards = this.originalCards.map((card) => {
        const clone = card.cloneNode(true)
        clone.dataset.loopCopyIndex = String(index)
        return clone
      })

      copies.push(...wrappedCards)
    }

    this.contentTarget.replaceChildren(...copies)
    this.contentTarget.dataset.loopPrepared = "true"
  }

  measureLoop() {
    this.segmentWidth = 0
    this.loopStart = 0

    if (!this.canLoop) return

    const firstCopyFirstCard = this.contentTarget.querySelector('.pod-array-item[data-loop-copy-index="0"]')
    const secondCopyFirstCard = this.contentTarget.querySelector('.pod-array-item[data-loop-copy-index="1"]')
    const centerCopyFirstCard = this.contentTarget.querySelector(`.pod-array-item[data-loop-copy-index="${this.cloneCopies}"]`)

    if (!firstCopyFirstCard || !secondCopyFirstCard || !centerCopyFirstCard) return

    this.segmentWidth = secondCopyFirstCard.offsetLeft - firstCopyFirstCard.offsetLeft
    this.loopStart = centerCopyFirstCard.offsetLeft
  }

  normalizeLoopPosition() {
    if (!this.canLoopSeamlessly) return

    const leftEdge = this.loopStart - this.segmentWidth
    const rightEdge = this.loopStart + this.segmentWidth

    if (this.scrollElement.scrollLeft < leftEdge) {
      this.scrollElement.scrollLeft += this.segmentWidth
    } else if (this.scrollElement.scrollLeft > rightEdge) {
      this.scrollElement.scrollLeft -= this.segmentWidth
    }
  }

  trackGap() {
    const styles = window.getComputedStyle(this.contentTarget)
    return Number.parseFloat(styles.columnGap || styles.gap || "0") || 0
  }

  get scrollElement() {
    return this.trackTarget
  }

  get firstCard() {
    return this.contentTarget.querySelector(".pod-array-item")
  }

  get originalCards() {
    if (!this._originalCards) {
      const template = document.createElement("template")
      template.innerHTML = this.originalMarkup
      this._originalCards = Array.from(template.content.children)
    }

    return this._originalCards
  }

  get normalizedLoopScrollLeft() {
    if (!this.canLoopSeamlessly) return 0

    const delta = this.scrollElement.scrollLeft - this.loopStart
    return ((delta % this.segmentWidth) + this.segmentWidth) % this.segmentWidth
  }

  get canLoop() {
    return this.loopValue && this.originalCards.length > 1
  }

  get canLoopSeamlessly() {
    return this.canLoop && this.segmentWidth > 0
  }
}
